import * as React from "react";
import { cn } from "@/lib/utils";

type TextButtonVariant =
   | "link"
   | "link-underline"
   | "cta"
   | "policy"
   | "footer"
   | "link-underline-white";

interface TextButtonProps
   extends React.ButtonHTMLAttributes<HTMLButtonElement> {
   variant: TextButtonVariant;
   children: React.ReactNode;
   selected?: boolean;
}

export function TextButton({
   variant,
   selected,
   children,
   className,
   ...props
}: TextButtonProps) {
   const [hovered, setHovered] = React.useState(false);

   const isSlideText = variant === "cta";
   const isUnderlineSlide =
      variant === "link-underline" || variant === "link-underline-white";
   const base =
      "inline-flex items-center gap-2 transition-colors whitespace-nowrap";

   const textButtonMap: Record<TextButtonVariant, string> = {
      link: cn(base, "text-d-body-m-line text-custom-dark hover:underline"),
      "link-underline": cn(
         base,
         "text-d-body-m-line text-custom-dark relative overflow-hidden"
      ),
      cta: cn(
         base,
         "text-d-body-l-paragraph text-custom-dark relative overflow-hidden"
      ),
      policy: cn(
         base,
         "text-d-body-m-line text-custom-dark/70 hover:text-custom-dark"
      ),
      footer: cn(base, "text-d-body-m-line text-custom-dark hover:underline"),
      "link-underline-white": cn(base, " text-white"),
   };

   const renderChildren = () => {
      if (isSlideText) {
         return (
            <span className="relative block h-[1.75rem] leading-none overflow-hidden">
               <span aria-hidden className="invisible inline-block">
                  {children}
               </span>
               <span
                  className={cn(
                     "absolute inset-0 transition-all duration-200 ease-in",
                     hovered
                        ? "translate-y-full opacity-0"
                        : "translate-y-0 opacity-100"
                  )}
               >
                  {children}
               </span>
               <span
                  className={cn(
                     "absolute inset-0 transition-all duration-200 ease-in",
                     hovered
                        ? "translate-y-0 opacity-100"
                        : "-translate-y-full opacity-0"
                  )}
               >
                  {children}
               </span>
            </span>
         );
      }

      if (isUnderlineSlide) {
         return (
            <span className="relative group">
               <span className="block">{children}</span>
               <span className="absolute bottom-0 left-0 h-[1px] w-full scale-x-0 origin-left bg-current transition-transform duration-300 group-hover:scale-x-100" />
            </span>
         );
      }

      return <>{children}</>;
   };

   return (
      <button
         type="button"
         className={cn(
            textButtonMap[variant],
            selected && "border-b border-current",
            "group",
            className
         )}
         onMouseEnter={() => setHovered(true)}
         onMouseLeave={() => setHovered(false)}
         {...props}
      >
         {renderChildren()}
      </button>
   );
}
